import { FC, KeyboardEvent } from 'react';
import classNames from 'classnames';
import moment from 'moment/moment';
import { Button as ReactAriaButton, Menu, MenuItem, MenuTrigger, Popover } from 'react-aria-components';
import ArrowDown from '@/assets/Icons/Arrow-Down.svg?react';
import NoDataIcon from '@/assets/Icons/No-Data.svg?react';
import SortIcon from '@/assets/Icons/Sort.svg?react';
import Checkbox from '@/components/ui/Checkbox/Checkbox.tsx';
import FlexBlock from '@/components/ui/FlexBlock/FlexBlock.tsx';
import Spinner from '@/components/ui/Spinner/Spinner.tsx';
import Tooltip from '@/components/ui/Tooltip/Tooltip.tsx';
import MissingFlag from '@/pages/MyClimate/TripEmissionLog/MissingFlag.tsx';
import {
	getDestinationDisplayName,
	getItineraryRowKey,
	getItinerarySelectionKey,
	SelectableVisibleItinerary,
	TripSelectionScope,
} from './actionNeededUtils.ts';
import styles from './TripEmissionLog.module.scss';
import DataComplianceMissingType = App.Enums.DataComplianceMissingType;
import DataComplianceUserItineraryData = App.Data.DataComplianceUserItineraryData;
import DataComplianceUserItineraryReferenceData = App.Data.DataComplianceUserItineraryReferenceData;

interface ActionNeededTripsTableProps {
	itineraries: DataComplianceUserItineraryData[];
	selectableItineraries: SelectableVisibleItinerary[];
	selectedItineraries: ReadonlyMap<string, DataComplianceUserItineraryReferenceData>;
	tripSelectionScope: TripSelectionScope | null;
	selectedTripsCount: number;
	allTripsOnPageSelected: boolean;
	someTripsOnPageSelected: boolean;
	selectedPeriodDataReady: boolean;
	isChangingRelationshipScope: boolean;
	fadingOutRowKey: string | null;
	shouldShowSpinner: boolean;
	selectionDisabled: boolean;
	shouldShowCurrentReportingPeriodTrips: boolean;
	currentReportingPeriodTripsCount: number;
	sortTripStartDate: string;
	onPageSelectionChange: (isSelected: boolean) => void;
	onSelectionScopeChange: (scope: TripSelectionScope) => void;
	onTripSelectionChange: (
		reference: DataComplianceUserItineraryReferenceData,
		complianceGapType: DataComplianceMissingType,
		isSelected: boolean,
	) => void;
	onToggleSort: () => void;
	onOpenTrip: (itinerary: DataComplianceUserItineraryData) => void;
	onViewCurrentReportingPeriodTrips: () => void;
}

const tripSelectionOptions: Array<{ value: TripSelectionScope; label: string }> = [
	{ value: 'all', label: 'Missing any details' },
	{ value: 'missing-transport', label: 'Missing transportation' },
	{ value: 'missing-hotel', label: 'Missing accommodation' },
];

const travelerTooltipText = 'The traveler is the person who travels.';
const travelArrangerTooltipText = 'The travel arranger is the person who booked the trip.';
const tripNamePreviewLength = 20;

const formatTripDateRange = (startAt: string, endAt: string) =>
	`${moment(startAt).format('DD.MM')} – ${moment(endAt).format('DD.MM.YY')}`;

const TripNameWithTooltip: FC<{ name: string }> = ({ name }) => {
	const isTruncated = name.length > tripNamePreviewLength;
	const displayName = isTruncated ? `${name.slice(0, tripNamePreviewLength)}...` : name;

	return (
		<span
			className={styles.tripName}
			tabIndex={isTruncated ? 0 : undefined}
			aria-label={isTruncated ? name : undefined}
		>
			{displayName}
			{isTruncated && (
				<span className={styles.tripNameTooltip} role="tooltip">
					{name}
				</span>
			)}
		</span>
	);
};

const ActionNeededTripsTable: FC<ActionNeededTripsTableProps> = ({
	itineraries,
	selectableItineraries,
	selectedItineraries,
	tripSelectionScope,
	selectedTripsCount,
	allTripsOnPageSelected,
	someTripsOnPageSelected,
	selectedPeriodDataReady,
	isChangingRelationshipScope,
	fadingOutRowKey,
	shouldShowSpinner,
	selectionDisabled,
	shouldShowCurrentReportingPeriodTrips,
	currentReportingPeriodTripsCount,
	sortTripStartDate,
	onPageSelectionChange,
	onSelectionScopeChange,
	onTripSelectionChange,
	onToggleSort,
	onOpenTrip,
	onViewCurrentReportingPeriodTrips,
}) => {
	const handleItineraryRowKeyDown = (
		event: KeyboardEvent<HTMLTableRowElement>,
		itinerary: DataComplianceUserItineraryData,
	) => {
		if (event.target !== event.currentTarget) {
			return;
		}

		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			onOpenTrip(itinerary);
		}
	};

	return (
		<div className={styles.tableResults} aria-busy={shouldShowSpinner}>
			{!itineraries.length && selectedPeriodDataReady && !isChangingRelationshipScope && !fadingOutRowKey && (
				<FlexBlock flexDirection="column" rowGap="7px" alignItems="center" className={styles.noDataOverlay}>
					<NoDataIcon />
					<p>No trips to review in the selected date range</p>
					{shouldShowCurrentReportingPeriodTrips && (
						<FlexBlock
							className={styles.currentReportingPeriodFallback}
							flexDirection="column"
							alignItems="center"
							rowGap="8px"
							aria-live="polite"
						>
							<p>
								You still have {currentReportingPeriodTripsCount}{' '}
								{currentReportingPeriodTripsCount === 1 ? 'trip' : 'trips'} needing details in your
								company’s annual reporting period.
							</p>
							<button type="button" onClick={onViewCurrentReportingPeriodTrips}>
								View trips
							</button>
						</FlexBlock>
					)}
				</FlexBlock>
			)}
			{shouldShowSpinner && (
				<div className={styles.spinnerWrapper} role="status" aria-label="Loading trips">
					<Spinner absolute overlay className={styles.tableSpinner} />
				</div>
			)}
			<div className={styles.tableContent}>
				<table>
					{!!itineraries.length && (
						<thead>
							<tr>
								<th className={styles.selectionColumn} scope="col">
									<div className={styles.selectionHeaderControl}>
										<div className={styles.selectPageControl}>
											<Checkbox
												className={styles.selectPageCheckbox}
												aria-label="Select page"
												aria-describedby="select-page-tooltip"
												isSelected={allTripsOnPageSelected}
												isIndeterminate={someTripsOnPageSelected}
												isDisabled={!selectableItineraries.length || selectionDisabled}
												onChange={onPageSelectionChange}
											/>
											<span
												id="select-page-tooltip"
												className={styles.selectPageTooltip}
												role="tooltip"
											>
												Select page
											</span>
										</div>
										<MenuTrigger>
											<ReactAriaButton
												className={styles.selectionMenuButton}
												aria-label="Choose which trips to select"
												isDisabled={!selectableItineraries.length || selectionDisabled}
											>
												<ArrowDown aria-hidden="true" />
											</ReactAriaButton>
											<Popover
												placement="bottom start"
												offset={6}
												className={styles.selectionMenuPopover}
											>
												<Menu aria-label="Select trips" className={styles.selectionMenu}>
													{tripSelectionOptions.map(option => {
														const hasMatchingTrips = selectableItineraries.some(
															item =>
																option.value === 'all' ||
																item.complianceGapType === option.value,
														);

														return (
															<MenuItem
																key={option.value}
																id={option.value}
																className={classNames(
																	styles.selectionMenuItem,
																	tripSelectionScope === option.value &&
																		selectedTripsCount > 0 &&
																		styles.selected,
																)}
																isDisabled={!hasMatchingTrips}
																onAction={() => onSelectionScopeChange(option.value)}
															>
																{option.label}
															</MenuItem>
														);
													})}
												</Menu>
											</Popover>
										</MenuTrigger>
									</div>
								</th>
								<th>
									<FlexBlock alignItems="center" columnGap="2px">
										Trip{' '}
										<button
											type="button"
											onClick={onToggleSort}
											className={styles.sort}
											aria-label={`Sort trips by start date ${sortTripStartDate === 'asc' ? 'descending' : 'ascending'}`}
										>
											<SortIcon />
										</button>
									</FlexBlock>
								</th>
								<th>
									<FlexBlock alignItems="center" columnGap="2px" className={styles.filter}>
										Travel arranger{' '}
										<div className={styles.tooltip}>
											<Tooltip text={travelArrangerTooltipText} />
										</div>
									</FlexBlock>
								</th>
								<th>
									<FlexBlock alignItems="center" columnGap="2px" className={styles.filter}>
										Traveler{' '}
										<div className={styles.tooltip}>
											<Tooltip text={travelerTooltipText} />
										</div>
									</FlexBlock>
								</th>
								<th>
									<FlexBlock alignItems="center" columnGap="2px" className={styles.filter}>
										Missing details
										<div className={styles.tooltip}>
											<Tooltip text="Shows whether accommodation or transport details are missing. Add the relevant details or confirm they weren’t needed." />
										</div>
									</FlexBlock>
								</th>
								<th></th>
							</tr>
						</thead>
					)}
					<tbody>
						{itineraries.map(item => {
							const rowKey = getItineraryRowKey(item);
							const selectionReference =
								item.travelerId == null ? null : { itineraryId: item.id, travelerId: item.travelerId };
							const isRowSelected =
								selectionReference !== null &&
								selectedItineraries.has(getItinerarySelectionKey(selectionReference));

							return (
								<tr
									key={rowKey}
									className={classNames(
										styles.itineraryRow,
										isRowSelected && styles.reviewDisabledRow,
										fadingOutRowKey === rowKey && styles.fadeOut,
									)}
									tabIndex={isRowSelected ? -1 : 0}
									aria-disabled={isRowSelected}
									onClick={() => !isRowSelected && onOpenTrip(item)}
									onKeyDown={event => !isRowSelected && handleItineraryRowKeyDown(event, item)}
								>
									<td
										className={styles.selectionColumn}
										onClick={event => event.stopPropagation()}
										onKeyDown={event => event.stopPropagation()}
									>
										<Checkbox
											className={styles.rowSelectionCheckbox}
											aria-label={`Select ${getDestinationDisplayName(item)} trip`}
											isSelected={isRowSelected}
											isDisabled={item.travelerId == null || selectionDisabled}
											onChange={isSelected => {
												if (selectionReference) {
													onTripSelectionChange(
														selectionReference,
														item.complianceGapType,
														isSelected,
													);
												}
											}}
										/>
									</td>
									<td className={styles.trip}>
										<div className={styles.tripCellContent}>
											<p className={styles.tripCellName}>
												<TripNameWithTooltip name={getDestinationDisplayName(item)} />
											</p>
											<p className={styles.tripCellDate}>
												{formatTripDateRange(item.startAt, item.endAt)}
											</p>
										</div>
									</td>
									<td>
										<span className={styles.rowCellContent}>{item.bookerName || '–'}</span>
									</td>
									<td>
										<span className={styles.rowCellContent}>{item.travelerName || '–'}</span>
									</td>
									<td>
										<MissingFlag missingItem={item.complianceGapType} />
									</td>
									<td>
										<button
											type="button"
											className={styles.completeBttn}
											disabled={isRowSelected}
											onClick={event => {
												event.stopPropagation();
												onOpenTrip(item);
											}}
										>
											Review
										</button>
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>
		</div>
	);
};

export default ActionNeededTripsTable;
